import { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { LogIn } from 'lucide-react';
import { useAuthStore } from '../../store/useAuthStore';
import { getRememberMePreference, setRememberMePreference } from '../../lib/supabase';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const signIn = useAuthStore((s) => s.signIn);

  // ?next= takes priority; fall back to react-router state (ProtectedRoute sets this)
  const nextParam = searchParams.get('next');
  const locationState = location.state as { from?: { pathname: string }; email?: string } | null;
  const from = nextParam ?? locationState?.from?.pathname ?? '/home';

  const [email, setEmail] = useState(locationState?.email ?? '');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(() => getRememberMePreference());
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  function handleRememberMeChange(checked: boolean) {
    setRememberMe(checked);
    setRememberMePreference(checked);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await signIn(email, password, rememberMe);
      navigate(from, { replace: true });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <h2 className="text-xl font-semibold text-gray-100 mb-1">Sign in</h2>
      <p className="text-sm text-gray-500 mb-6">Welcome back to Navyrx Sports</p>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-loss/10 border border-loss/25 text-sm text-loss">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1.5" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
            placeholder="you@example.com"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="block text-xs font-medium text-gray-400" htmlFor="password">
              Password
            </label>
            <Link
              to="/forgot-password"
              className="text-xs text-brand hover:text-brand-light transition-colors"
            >
              Forgot password?
            </Link>
          </div>
          <input
            id="password"
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-3 py-2 bg-surface-2 border border-surface-4 rounded-md text-sm text-gray-100 placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
            placeholder="••••••••"
          />
        </div>

        <div className="flex items-center justify-between gap-3 -mt-1">
          <label
            htmlFor="remember-me"
            className="flex min-h-10 cursor-pointer items-center gap-3 rounded-md text-sm text-gray-300"
          >
            <input
              id="remember-me"
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => handleRememberMeChange(e.target.checked)}
              aria-describedby="remember-me-description"
              className="h-4 w-4 rounded border-surface-4 bg-surface-2 text-brand accent-brand focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 focus:ring-offset-surface-1"
            />
            <span className="font-medium">Remember Me</span>
          </label>
          <span id="remember-me-description" className="sr-only">
            Keep this session active after the browser closes on this device.
          </span>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="btn-primary w-full flex items-center justify-center gap-2 py-2.5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <LogIn className="w-4 h-4" />
          {loading ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <div className="mt-5 grid grid-cols-1 gap-2">
        {['Google', 'GitHub', 'Discord'].map((provider) => (
          <button
            key={provider}
            type="button"
            disabled
            className="w-full px-3 py-2 rounded-md border border-surface-4 text-sm text-gray-500 bg-surface-2 cursor-not-allowed"
          >
            Continue with {provider} (coming soon)
          </button>
        ))}
      </div>

      <p className="mt-6 text-sm text-center text-gray-500">
        Don&apos;t have an account?{' '}
        <Link to="/signup" className="text-brand hover:text-brand-light transition-colors">
          Create one
        </Link>
      </p>
    </>
  );
}
